import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search, Eye, Trash2, X, MessageSquare, Lightbulb, AlertTriangle, Calendar, MapPin, Users,
  CheckCircle2, Ban, Clock3, Tag as TagIcon, Heart, RefreshCw, ArrowUpDown, AlertCircle,
  CalendarClock, ExternalLink, RotateCcw, Megaphone, FileText, Loader2,
} from "lucide-react";
import moment from "moment";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminLoginAPi, deleteWithToken, fetchWithToken } from "api/ApiHandler";
import Sidebar1 from "components/Sidebar1";
import DEfulatImg from "../../Assests/Images/DefulatLogo.png";
import { useDialog, DialogFrame, DialogSection } from "../Admindashboard/ui";
import { FONT_HEADING, PAGE_SIZES, fromNow, handleAuthError, Kpi, Pagination, EmptyState } from "../userManagement/shared";

// ─── Helpers ────────────────────────────────────────────────────────────────

const getAvatarUrl = (userId, { forceDefaultForServiceProvider = false } = {}) => {
  if (forceDefaultForServiceProvider && userId?.role === "service_provider") return DEfulatImg;
  const path = userId?.imagePath || userId?.image;
  if (!path || path === "image") return DEfulatImg;
  return path;
};

const getAuthorName = (userId) => {
  if (!userId) return "Unknown";
  if (userId.businessName) return userId.businessName;
  const name = `${userId.firstName || ""} ${userId.lastName || ""}`.trim();
  return name || "Unknown";
};

const roleLabel = (role) => (role === "service_provider" ? "Service provider" : role === "customer" || !role ? "Customer" : role.replace(/_/g, " "));

const formatDate = (value, withTime = false) => {
  if (!value) return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  return m.format(withTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const m = moment.utc(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const eventStatusKey = (status) => {
  const s = (status || "pending").toLowerCase();
  return s === "approved" || s === "live" ? "live" : s === "rejected" ? "rejected" : "pending";
};

const EVENT_STATUS = {
  pending: { label: "Pending", badge: "bg-[#FEF3C7] text-[#B45309]", dot: "#F59E0B" },
  live: { label: "Live", badge: "bg-[#DCFCE7] text-[#16A34A]", dot: "#16A34A" },
  rejected: { label: "Rejected", badge: "bg-[#FDECEA] text-[#EA352B]", dot: "#EA352B" },
};

const POST_TYPES = {
  tip: { label: "Tip", Icon: Lightbulb, badge: "bg-[#DBEAFE] text-[#2563EB]" },
  alert: { label: "Alert", Icon: AlertTriangle, badge: "bg-[#FFEDD5] text-[#EA580C]" },
  post: { label: "Post", Icon: FileText, badge: "bg-[#F3F4F6] text-[#4B5563]" },
};
const postTypeMeta = (t) => POST_TYPES[(t || "post").toLowerCase()] || { ...POST_TYPES.post, label: t };

const clamp = (lines) => ({ display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" });

const POST_SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  liked: { label: "Most liked", fn: (a, b) => (b.likes?.length || 0) - (a.likes?.length || 0) },
};
const EVENT_SORTS = {
  soonest: { label: "Event date (soonest)", fn: (a, b) => new Date(a.eventDateTime) - new Date(b.eventDateTime) },
  newest: { label: "Recently created", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  popular: { label: "Most joined", fn: (a, b) => (b.going?.length || 0) - (a.going?.length || 0) },
};

// ─── Page ───────────────────────────────────────────────────────────────────

export default function CommunityManagement() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [postType, setPostType] = useState("all");
  const [eventStatus, setEventStatus] = useState("all");
  const [postSort, setPostSort] = useState("newest");
  const [eventSort, setEventSort] = useState("soonest");
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'post' | 'event', id, title }
  const [busy, setBusy] = useState(null); // `${action}:${id}` while a request runs

  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState("");

  const [events, setEvents] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const loadPosts = async () => {
    try {
      setIsPostsLoading(true);
      setPostsError("");
      const data = await fetchWithToken("/admin/get-all-posts");
      setPosts(Array.isArray(data?.posts) ? data.posts : []);
    } catch (error) {
      console.error("Fetch posts error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setPostsError("Unable to load posts.");
    } finally {
      setIsPostsLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setIsEventsLoading(true);
      setEventsError("");
      const data = await fetchWithToken("/admin/get-all-events");
      setEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (error) {
      console.error("Fetch events error:", error.response?.data || error);
      if (handleAuthError(error)) return;
      setEventsError("Unable to load events.");
    } finally {
      setIsEventsLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    loadEvents();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, activeTab, postType, eventStatus, postSort, eventSort, pageSize]);

  // ── Actions (same endpoints as before) ──
  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const approveEvent = (id) => run(`approve:${id}`, async () => {
    try {
      await AdminLoginAPi("/admin/approve-event", { id });
      toast.success("Event approved and is now live.");
      setSelectedEvent((prev) => (prev && prev._id === id ? { ...prev, status: "approved" } : prev));
      loadEvents();
    } catch (error) {
      console.error("Approve event error:", error.response?.data || error);
      handleAuthError(error);
      // error toast is already shown by the global axios interceptor
    }
  });

  const rejectEvent = (id) => run(`reject:${id}`, async () => {
    try {
      await AdminLoginAPi("/admin/reject-event", { id });
      toast.success("Event rejected.");
      setSelectedEvent((prev) => (prev && prev._id === id ? { ...prev, status: "rejected" } : prev));
      loadEvents();
    } catch (error) {
      console.error("Reject event error:", error.response?.data || error);
      handleAuthError(error);
    }
  });

  const deleteEvent = async (id) => {
    try {
      await deleteWithToken(`/admin/delete-event/${id}`);
      toast.success("Event deleted.");
      setSelectedEvent((prev) => (prev && prev._id === id ? null : prev));
      loadEvents();
    } catch (error) {
      console.error("Delete event error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const deletePost = async (id) => {
    try {
      await deleteWithToken(`/admin/delete-post/${id}`);
      toast.success("Post deleted.");
      setSelectedPost((prev) => (prev && prev._id === id ? null : prev));
      loadPosts();
    } catch (error) {
      console.error("Delete post error:", error.response?.data || error);
      handleAuthError(error);
    }
  };

  const confirmDelete = () => {
    if (!confirmTarget) return;
    const { type, id } = confirmTarget;
    run(`delete:${id}`, async () => {
      if (type === "post") await deletePost(id);
      else await deleteEvent(id);
      setConfirmTarget(null);
    });
  };

  // ── Stats ──
  const postStats = useMemo(() => {
    const weekAgo = moment().subtract(7, "days");
    return {
      total: posts.length,
      tips: posts.filter((p) => (p.postType || "").toLowerCase() === "tip").length,
      alerts: posts.filter((p) => (p.postType || "").toLowerCase() === "alert").length,
      likes: posts.reduce((s, p) => s + (p.likes?.length || 0), 0),
      thisWeek: posts.filter((p) => moment.utc(p.createdAt).isAfter(weekAgo)).length,
    };
  }, [posts]);

  const eventCounts = useMemo(() => {
    const c = { all: events.length, pending: 0, live: 0, rejected: 0, upcoming: 0 };
    events.forEach((e) => {
      c[eventStatusKey(e.status)] += 1;
      if (eventStatusKey(e.status) === "live" && moment(e.eventDateTime).isAfter(moment())) c.upcoming += 1;
    });
    return c;
  }, [events]);

  const postTypeCounts = useMemo(() => {
    const c = { all: posts.length };
    posts.forEach((p) => {
      const k = (p.postType || "post").toLowerCase();
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [posts]);

  // ── Filtering ──
  const filteredPosts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return posts
      .filter((p) => {
        const matchesTerm =
          !term ||
          (p.description || "").toLowerCase().includes(term) ||
          getAuthorName(p.userId).toLowerCase().includes(term) ||
          (p.tags || []).some((t) => String(t).toLowerCase().includes(term));
        const matchesDate = !dateFilter || toDateInputValue(p.createdAt) === dateFilter;
        const matchesType = postType === "all" || (p.postType || "post").toLowerCase() === postType;
        return matchesTerm && matchesDate && matchesType;
      })
      .sort(POST_SORTS[postSort].fn);
  }, [posts, searchTerm, dateFilter, postType, postSort]);

  const filteredEvents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return events
      .filter((e) => {
        const matchesTerm =
          !term ||
          (e.eventName || "").toLowerCase().includes(term) ||
          (e.location?.text || "").toLowerCase().includes(term) ||
          getAuthorName(e.userId).toLowerCase().includes(term);
        const matchesDate = !dateFilter || toDateInputValue(e.eventDateTime) === dateFilter;
        const matchesStatus = eventStatus === "all" || eventStatusKey(e.status) === eventStatus;
        return matchesTerm && matchesDate && matchesStatus;
      })
      .sort(EVENT_SORTS[eventSort].fn);
  }, [events, searchTerm, dateFilter, eventStatus, eventSort]);

  const isPosts = activeTab === "posts";
  const activeList = isPosts ? filteredPosts : filteredEvents;
  const isLoading = isPosts ? isPostsLoading : isEventsLoading;
  const loadError = isPosts ? postsError : eventsError;
  const totalPages = Math.max(1, Math.ceil(activeList.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const currentItems = activeList.slice(pageStart, pageStart + pageSize);
  const hasFilters = searchTerm || dateFilter || (isPosts ? postType !== "all" : eventStatus !== "all");

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("");
    setPostType("all");
    setEventStatus("all");
  };

  const refresh = () => (isPosts ? loadPosts() : loadEvents());

  return (
    <div className="flex h-screen bg-[#F6F6F8] overflow-hidden">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <Sidebar1 isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      <main className="flex-1 min-w-0 overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-6 sm:flex-col sm:items-start">
          <div>
            <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>Community</h1>
            <p className="m-0 mt-1 text-sm text-[#6B7280]">Moderate posts shared in the app and review events before they go live.</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-4 py-2.5 text-sm font-medium text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition disabled:opacity-60"
          >
            <RefreshCw size={15} className={isLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </header>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-3 mb-6 max-w-[560px] sm:max-w-none" role="tablist">
          <TabCard
            active={isPosts}
            onClick={() => setActiveTab("posts")}
            Icon={MessageSquare}
            label="Posts"
            count={isPostsLoading ? "…" : posts.length}
            hint={`${postStats.thisWeek} this week`}
          />
          <TabCard
            active={!isPosts}
            onClick={() => setActiveTab("events")}
            Icon={Calendar}
            label="Events"
            count={isEventsLoading ? "…" : events.length}
            hint={eventCounts.pending ? `${eventCounts.pending} waiting for approval` : "Nothing to review"}
            alert={eventCounts.pending > 0}
          />
        </div>

        {/* KPIs */}
        {isPosts ? (
          <section className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
            <Kpi Icon={MessageSquare} label="Total posts" value={postStats.total} sub={`${postStats.thisWeek} in the last 7 days`} loading={isPostsLoading} />
            <Kpi Icon={Lightbulb} label="Tips shared" value={postStats.tips} loading={isPostsLoading} />
            <Kpi Icon={AlertTriangle} label="Alerts shared" value={postStats.alerts} loading={isPostsLoading} />
            <Kpi Icon={Heart} label="Total likes" value={postStats.likes} sub={postStats.total ? `${(postStats.likes / postStats.total).toFixed(1)} per post` : undefined} loading={isPostsLoading} />
          </section>
        ) : (
          <section className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
            <Kpi Icon={Clock3} label="Pending approval" value={eventCounts.pending} sub={eventCounts.pending ? "Needs your review" : "All caught up"} loading={isEventsLoading} />
            <Kpi Icon={CheckCircle2} label="Live events" value={eventCounts.live} sub={`${eventCounts.upcoming} upcoming`} loading={isEventsLoading} />
            <Kpi Icon={Ban} label="Rejected" value={eventCounts.rejected} loading={isEventsLoading} />
            <Kpi Icon={Users} label="People joined" value={events.reduce((s, e) => s + (e.going?.length || 0), 0)} loading={isEventsLoading} />
          </section>
        )}

        {/* List card */}
        <section className="bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4" style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}>
          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[220px] sm:min-w-0 sm:w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
              {/* No `type` attr: styles/tailwind.css resets padding/border on typed inputs */}
              <input
                role="searchbox"
                enterKeyHint="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
                placeholder={isPosts ? "Search posts, authors or tags" : "Search events, organizers or locations"}
                className="w-full pl-10 pr-9 py-2.5 bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl text-sm text-[#111] placeholder-[#9CA3AF] focus:outline-none focus:border-[#EA352B] focus:bg-[#fff] focus:ring-0 transition"
              />
              {searchTerm && (
                <button type="button" aria-label="Clear search" onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]">
                  <X size={15} />
                </button>
              )}
            </div>

            <div className="relative sm:flex-1">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={dateFilter ? moment(dateFilter) : null}
                  onChange={(v) => setDateFilter(v && v.isValid() ? v.format("YYYY-MM-DD") : "")}
                  format="DD MMM YYYY"
                  slotProps={{
                    field: { clearable: true, onClear: () => setDateFilter("") },
                    textField: {
                      size: "small",
                      placeholder: isPosts ? "Posted on" : "Event date",
                      sx: {
                        width: { xs: "100%", sm: 190 },
                        "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#F7F7F9", fontSize: "14px", fontFamily: "'NeueMontreal', sans-serif" },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#EEEEF1" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D9DAE0" },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#EA352B !important", borderWidth: "1px !important" },
                        "& .MuiInputBase-input": { padding: "9.5px 12px" },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <label className="relative inline-flex items-center sm:flex-1">
              <ArrowUpDown size={14} className="absolute left-3 text-[#9CA3AF] pointer-events-none" />
              <select
                value={isPosts ? postSort : eventSort}
                onChange={(e) => (isPosts ? setPostSort : setEventSort)(e.target.value)}
                aria-label="Sort"
                className="w-full appearance-none bg-[#F7F7F9] border border-[#EEEEF1] rounded-xl pl-8 pr-8 py-2.5 text-sm text-[#374151] focus:outline-none focus:border-[#EA352B] focus:ring-0 cursor-pointer"
              >
                {Object.entries(isPosts ? POST_SORTS : EVENT_SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
              </select>
            </label>
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-2 flex-wrap mb-5">
            {isPosts
              ? Object.keys(postTypeCounts).map((k) => (
                <Chip
                  key={k}
                  active={postType === k}
                  onClick={() => setPostType(k)}
                  label={k === "all" ? "All" : postTypeMeta(k).label}
                  count={postTypeCounts[k]}
                />
              ))
              : ["all", "pending", "live", "rejected"].map((k) => (
                <Chip
                  key={k}
                  active={eventStatus === k}
                  onClick={() => setEventStatus(k)}
                  label={k === "all" ? "All" : EVENT_STATUS[k].label}
                  count={eventCounts[k]}
                  dot={k === "all" ? null : EVENT_STATUS[k].dot}
                />
              ))}
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="ml-auto text-xs font-semibold text-[#EA352B] hover:text-[#C92A21]">
                Clear filters
              </button>
            )}
          </div>

          {/* Content */}
          {loadError ? (
            <EmptyState
              Icon={AlertCircle}
              title={loadError}
              text="Check your connection and try again."
              action={<button type="button" onClick={refresh} className="mt-3 rounded-xl bg-[#EA352B] text-[#fff] px-4 py-2 text-sm font-medium">Try again</button>}
            />
          ) : isLoading ? (
            isPosts ? (
              <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                {Array.from({ length: 4 }).map((_, i) => <PostCardSkeleton key={i} />)}
              </div>
            ) : (
              <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <EventRowSkeleton key={i} />)}</div>
            )
          ) : currentItems.length === 0 ? (
            <EmptyState
              Icon={isPosts ? MessageSquare : Calendar}
              title={isPosts ? "No posts found" : "No events found"}
              text={hasFilters ? "Try a different search or filter." : isPosts ? "Posts from the app will appear here." : "Events created in the app will appear here."}
            />
          ) : isPosts ? (
            <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
              {currentItems.map((p) => (
                <PostCard
                  key={p._id}
                  post={p}
                  onOpen={() => setSelectedPost(p)}
                  onDelete={() => setConfirmTarget({ type: "post", id: p._id, title: getAuthorName(p.userId) })}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {currentItems.map((e) => (
                <EventRow
                  key={e._id}
                  event={e}
                  busy={busy}
                  onOpen={() => setSelectedEvent(e)}
                  onApprove={() => approveEvent(e._id)}
                  onReject={() => rejectEvent(e._id)}
                  onDelete={() => setConfirmTarget({ type: "event", id: e._id, title: e.eventName })}
                />
              ))}
            </div>
          )}

          {!isLoading && !loadError && activeList.length > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap mt-6 pt-4 border-t border-[#F1F1F4]">
              <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                <span>
                  Showing <b className="text-[#111]">{pageStart + 1}–{Math.min(pageStart + pageSize, activeList.length)}</b> of{" "}
                  <b className="text-[#111]">{activeList.length}</b>
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Items per page"
                  className="bg-[#F7F7F9] border border-[#EEEEF1] rounded-lg py-1 pl-2 pr-7 text-xs focus:outline-none focus:ring-0 focus:border-[#EA352B]"
                >
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
              </div>
              {totalPages > 1 && <Pagination page={currentPage} total={totalPages} onChange={setCurrentPage} />}
            </div>
          )}
        </section>
      </main>

      <PostDialog
        post={selectedPost}
        paused={!!confirmTarget}
        onClose={() => setSelectedPost(null)}
        onDelete={() => setConfirmTarget({ type: "post", id: selectedPost._id, title: getAuthorName(selectedPost.userId) })}
      />
      <EventDialog
        event={selectedEvent}
        paused={!!confirmTarget}
        busy={busy}
        onClose={() => setSelectedEvent(null)}
        onApprove={() => approveEvent(selectedEvent._id)}
        onReject={() => rejectEvent(selectedEvent._id)}
        onDelete={() => setConfirmTarget({ type: "event", id: selectedEvent._id, title: selectedEvent.eventName })}
      />
      <ConfirmDelete
        target={confirmTarget}
        busy={confirmTarget && busy === `delete:${confirmTarget.id}`}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// ─── List pieces ────────────────────────────────────────────────────────────

function TabCard({ active, onClick, Icon, label, count, hint, alert }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`relative text-left rounded-2xl border p-4 transition-all ${
        active ? "bg-[#1B1B22] border-[#1B1B22] text-[#fff] shadow-[0_10px_30px_rgba(27,27,34,0.25)]" : "bg-[#fff] border-[#EEEEF1] text-[#111] hover:border-[#D9DAE0]"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#EA352B]" : "bg-[#FDECEA]"}`}>
          <Icon size={18} className={active ? "text-[#fff]" : "text-[#EA352B]"} />
        </span>
        <span className="text-2xl font-semibold">{count}</span>
      </div>
      <p className={`m-0 mt-3 text-sm font-semibold ${active ? "text-[#fff]" : "text-[#111]"}`}>{label}</p>
      <p className={`m-0 text-[11px] ${alert ? (active ? "text-[#FCA5A5]" : "text-[#EA352B]") : active ? "text-[#ffffff99]" : "text-[#9CA3AF]"}`}>
        {alert && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EA352B] mr-1.5 align-middle animate-pulse" />}
        {hint}
      </p>
    </button>
  );
}

function Chip({ active, onClick, label, count, dot }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
        active ? "bg-[#EA352B] text-[#fff] shadow-sm" : "bg-[#F3F3F6] text-[#6B7280] hover:text-[#111]"
      }`}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "#fff" : dot }} />}
      {label}
      <span className={`rounded-full px-1.5 py-px text-[10px] ${active ? "bg-[#ffffff33]" : "bg-[#fff]"}`}>{count}</span>
    </button>
  );
}

function Avatar({ src, size = 36, className = "" }) {
  return (
    <img
      src={src}
      onError={(e) => { e.currentTarget.src = DEfulatImg; }}
      alt=""
      className={`rounded-full object-cover shrink-0 bg-[#F3F3F6] ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

function TypeBadge({ type }) {
  const m = postTypeMeta(type);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${m.badge}`}>
      <m.Icon size={11} /> {m.label}
    </span>
  );
}

function EventStatusBadge({ status }) {
  const m = EVENT_STATUS[eventStatusKey(status)];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${m.badge}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} /> {m.label}
    </span>
  );
}

function PostImage({ src, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return <img src={src} alt="" onError={() => setFailed(true)} className={`object-cover ${className}`} />;
}

function PostCard({ post, onOpen, onDelete }) {
  const likes = post.likes?.length || 0;
  return (
    <article className="group rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] hover:bg-[#fff] hover:border-[#F6C9C5] hover:shadow-[0_8px_24px_rgba(234,53,43,0.08)] transition-all flex flex-col overflow-hidden">
      <button type="button" onClick={onOpen} className="text-left p-4 flex-1">
        <div className="flex items-center gap-3">
          <Avatar src={getAvatarUrl(post.userId)} />
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-medium text-[#111] truncate">{getAuthorName(post.userId)}</p>
            <p className="m-0 text-[11px] text-[#9CA3AF]">{roleLabel(post.userId?.role)} · {fromNow(post.createdAt)}</p>
          </div>
          <TypeBadge type={post.postType} />
        </div>

        <div className="flex gap-3 mt-3">
          <p className="m-0 flex-1 text-[13px] text-[#374151] leading-relaxed whitespace-pre-line" style={clamp(3)}>
            {post.description || <span className="text-[#9CA3AF] italic">No text</span>}
          </p>
          {post.image && <PostImage src={post.image} className="w-20 h-20 rounded-xl shrink-0" />}
        </div>

        {post.tags?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-3">
            {post.tags.slice(0, 4).map((t) => (
              <span key={t} className="rounded-full bg-[#fff] border border-[#EEEEF1] px-2 py-0.5 text-[11px] text-[#6B7280]">#{t}</span>
            ))}
            {post.tags.length > 4 && <span className="text-[11px] text-[#9CA3AF]">+{post.tags.length - 4}</span>}
          </div>
        )}
      </button>

      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-[#EEEEF1]">
        <span className="inline-flex items-center gap-1.5 text-xs text-[#6B7280]">
          <Heart size={14} className={likes ? "text-[#EA352B] fill-[#EA352B]" : "text-[#C0C4CC]"} /> {likes} {likes === 1 ? "like" : "likes"}
        </span>
        <div className="flex gap-1.5">
          <IconBtn Icon={Eye} label="View post" onClick={onOpen} />
          <IconBtn Icon={Trash2} label="Delete post" onClick={onDelete} danger />
        </div>
      </div>
    </article>
  );
}

function IconBtn({ Icon, label, onClick, danger, success, loading, disabled }) {
  const tone = danger
    ? "text-[#EA352B] hover:bg-[#FDECEA]"
    : success
      ? "text-[#16A34A] hover:bg-[#DCFCE7]"
      : "text-[#374151] hover:bg-[#F3F3F6]";
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition disabled:opacity-50 ${tone}`}
    >
      {loading ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />}
    </button>
  );
}

function DateTile({ value, large }) {
  const m = moment(value);
  const valid = m.isValid();
  return (
    <div className={`shrink-0 rounded-2xl overflow-hidden text-center border border-[#EEEEF1] bg-[#fff] ${large ? "w-20" : "w-14"}`}>
      <div className={`bg-[#EA352B] text-[#fff] font-semibold uppercase tracking-wider ${large ? "text-[11px] py-1" : "text-[9px] py-0.5"}`}>
        {valid ? m.format("MMM") : "—"}
      </div>
      <div className={`font-semibold text-[#111] leading-none ${large ? "text-3xl pt-2" : "text-xl pt-1.5"}`}>{valid ? m.format("D") : "?"}</div>
      <div className={`text-[#9CA3AF] ${large ? "text-[11px] pb-2 pt-1" : "text-[9px] pb-1.5 pt-0.5"}`}>{valid ? m.format("ddd") : ""}</div>
    </div>
  );
}

function EventRow({ event: e, busy, onOpen, onApprove, onReject, onDelete }) {
  const key = eventStatusKey(e.status);
  const past = moment(e.eventDateTime).isBefore(moment());
  return (
    <div className="group flex items-center gap-4 sm:gap-3 rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] hover:bg-[#fff] hover:border-[#F6C9C5] hover:shadow-[0_8px_24px_rgba(234,53,43,0.06)] p-3 transition-all sm:flex-wrap">
      <button type="button" onClick={onOpen} className="flex items-center gap-4 sm:gap-3 flex-1 min-w-0 text-left">
        <DateTile value={e.eventDateTime} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="m-0 text-sm font-semibold text-[#111] truncate group-hover:text-[#EA352B] transition-colors">{e.eventName || "Untitled event"}</p>
            {past && key === "live" && <span className="rounded-full bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5 text-[10px] font-semibold">Ended</span>}
          </div>
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mt-1 text-[12px] text-[#6B7280]">
            <span className="inline-flex items-center gap-1"><Clock3 size={12} /> {moment(e.eventDateTime).isValid() ? moment(e.eventDateTime).format("hh:mm A") : "—"}</span>
            <span className="inline-flex items-center gap-1 min-w-0 max-w-[260px]"><MapPin size={12} className="shrink-0" /> <span className="truncate">{e.location?.text || "No location"}</span></span>
            <span className="inline-flex items-center gap-1"><Users size={12} /> {e.going?.length || 0} going</span>
          </div>
          <p className="m-0 mt-1 text-[11px] text-[#9CA3AF] flex items-center gap-1.5">
            <Avatar src={getAvatarUrl(e.userId, { forceDefaultForServiceProvider: true })} size={16} />
            by {getAuthorName(e.userId)}
          </p>
        </div>
      </button>

      <div className="flex items-center gap-2 shrink-0 sm:w-full sm:justify-between sm:pl-[68px]">
        <EventStatusBadge status={e.status} />
        <div className="flex gap-1">
          {key !== "live" && <IconBtn Icon={CheckCircle2} label="Approve" onClick={onApprove} success loading={busy === `approve:${e._id}`} disabled={!!busy} />}
          {key === "pending" && <IconBtn Icon={Ban} label="Reject" onClick={onReject} danger loading={busy === `reject:${e._id}`} disabled={!!busy} />}
          <IconBtn Icon={Eye} label="View event" onClick={onOpen} />
          <IconBtn Icon={Trash2} label="Delete event" onClick={onDelete} danger disabled={!!busy} />
        </div>
      </div>
    </div>
  );
}

function PostCardSkeleton() {
  return (
    <div className="rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#F1F1F4] animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-28 bg-[#F1F1F4] rounded animate-pulse" />
          <div className="h-2 w-20 bg-[#F1F1F4] rounded animate-pulse" />
        </div>
      </div>
      <div className="h-3 w-full bg-[#F1F1F4] rounded animate-pulse" />
      <div className="h-3 w-4/5 bg-[#F1F1F4] rounded animate-pulse" />
    </div>
  );
}

function EventRowSkeleton() {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-[#EEEEF1] bg-[#FAFAFB] p-3">
      <div className="w-14 h-[68px] rounded-2xl bg-[#F1F1F4] animate-pulse" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-48 bg-[#F1F1F4] rounded animate-pulse" />
        <div className="h-2.5 w-72 max-w-full bg-[#F1F1F4] rounded animate-pulse" />
      </div>
      <div className="h-6 w-16 bg-[#F1F1F4] rounded-full animate-pulse" />
    </div>
  );
}

// ─── Dialogs ────────────────────────────────────────────────────────────────

function DialogHeader({ children, onClose, closeRef }) {
  return (
    <div className="relative shrink-0 px-6 pt-6 pb-5 sm:px-4 text-[#fff] overflow-hidden" style={{ background: "linear-gradient(120deg, #1B1B22 0%, #2A1614 60%, #EA352B 140%)" }}>
      <div className="pointer-events-none absolute -right-12 -top-16 w-52 h-52 rounded-full bg-[#EA352B] opacity-20" />
      <button
        ref={closeRef}
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute z-10 top-4 right-4 w-9 h-9 rounded-xl bg-[#ffffff1f] hover:bg-[#ffffff33] flex items-center justify-center transition"
      >
        <X size={18} className="text-[#fff]" />
      </button>
      <div className="relative pr-10">{children}</div>
    </div>
  );
}

function DialogFooter({ children }) {
  return <div className="shrink-0 flex gap-3 flex-wrap justify-end px-6 py-4 sm:px-4 bg-[#fff] border-t border-[#EEEEF1]">{children}</div>;
}

function ActionBtn({ Icon, label, onClick, variant = "neutral", loading, disabled }) {
  const styles = {
    danger: "bg-[#EA352B] text-[#fff] hover:bg-[#D42F26]",
    success: "bg-[#16A34A] text-[#fff] hover:bg-[#15803D]",
    outline: "border border-[#EEEEF1] text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B]",
    neutral: "bg-[#F3F3F6] text-[#374151] hover:bg-[#E9E9EE]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60 sm:flex-1 ${styles[variant]}`}
    >
      {loading ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} {label}
    </button>
  );
}

function PostDialog({ post, paused, onClose, onDelete }) {
  const closeRef = useRef(null);
  useDialog(!!post && !paused, onClose);
  useEffect(() => { if (post) closeRef.current?.focus(); }, [post]);
  if (!post) return null;
  const likes = post.likes?.length || 0;

  return (
    <DialogFrame labelledBy="post-dialog-title" onClose={onClose} maxWidth={680}>
      <DialogHeader onClose={onClose} closeRef={closeRef}>
        <div className="flex items-center gap-3">
          <Avatar src={getAvatarUrl(post.userId)} size={52} className="border-2 border-[#ffffff33]" />
          <div className="min-w-0">
            <h2 id="post-dialog-title" className="m-0 text-lg font-medium truncate" style={FONT_HEADING}>{getAuthorName(post.userId)}</h2>
            <p className="m-0 text-xs text-[#ffffffb3]">{roleLabel(post.userId?.role)} · {formatDate(post.createdAt, true)}</p>
          </div>
        </div>
      </DialogHeader>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <TypeBadge type={post.postType} />
          <span className="inline-flex items-center gap-1 rounded-full bg-[#fff] border border-[#EEEEF1] px-2.5 py-1 text-[11px] font-semibold text-[#374151]">
            <Heart size={11} className="text-[#EA352B] fill-[#EA352B]" /> {likes} {likes === 1 ? "like" : "likes"}
          </span>
          <span className="text-[11px] text-[#9CA3AF]">{fromNow(post.createdAt)}</span>
        </div>

        <DialogSection title="Post">
          <p className="m-0 text-[15px] text-[#374151] leading-relaxed whitespace-pre-line">
            {post.description || <span className="text-[#9CA3AF] italic">No text</span>}
          </p>
          {post.image && (
            <div className="mt-4 rounded-2xl overflow-hidden border border-[#EEEEF1] bg-[#F7F7F9]">
              <PostImage src={post.image} className="w-full max-h-[360px]" />
            </div>
          )}
        </DialogSection>

        {post.tags?.length > 0 && (
          <DialogSection title="Tags">
            <div className="flex gap-2 flex-wrap">
              {post.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#F7F7F9] px-3 py-1 text-xs text-[#374151]"><TagIcon size={11} className="text-[#EA352B]" /> {t}</span>
              ))}
            </div>
          </DialogSection>
        )}
      </div>

      <DialogFooter>
        <ActionBtn Icon={X} label="Close" onClick={onClose} variant="outline" />
        <ActionBtn Icon={Trash2} label="Delete post" onClick={onDelete} variant="danger" />
      </DialogFooter>
    </DialogFrame>
  );
}

function EventDialog({ event: e, paused, busy, onClose, onApprove, onReject, onDelete }) {
  const closeRef = useRef(null);
  useDialog(!!e && !paused, onClose);
  useEffect(() => { if (e) closeRef.current?.focus(); }, [e]);
  if (!e) return null;

  const key = eventStatusKey(e.status);
  const when = moment(e.eventDateTime);
  const going = Array.isArray(e.going) ? e.going : [];
  const goingUsers = going.filter((g) => g && typeof g === "object");
  const image = e.image || e.eventImage || e.coverImage;
  const mapsUrl = e.location?.text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location.text)}` : null;

  return (
    <DialogFrame labelledBy="event-dialog-title" onClose={onClose} maxWidth={760}>
      <DialogHeader onClose={onClose} closeRef={closeRef}>
        <div className="flex items-center gap-4">
          <DateTile value={e.eventDateTime} large />
          <div className="min-w-0">
            <EventStatusBadge status={e.status} />
            <h2 id="event-dialog-title" className="m-0 mt-2 text-2xl sm:text-xl font-medium" style={FONT_HEADING}>{e.eventName || "Untitled event"}</h2>
            <p className="m-0 mt-1 text-xs text-[#ffffffb3] flex items-center gap-1.5">
              <Avatar src={getAvatarUrl(e.userId, { forceDefaultForServiceProvider: true })} size={18} />
              Organized by {getAuthorName(e.userId)}
            </p>
          </div>
        </div>
      </DialogHeader>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 sm:p-4 space-y-4">
        {key === "pending" && (
          <div className="flex items-center gap-3 rounded-2xl bg-[#FFFBEB] border border-[#FDE68A] px-4 py-3">
            <Megaphone size={18} className="text-[#B45309] shrink-0" />
            <p className="m-0 text-sm text-[#78350F]">This event isn’t visible in the app yet. Approve it to make it live, or reject it.</p>
          </div>
        )}

        {image && (
          <div className="rounded-2xl overflow-hidden border border-[#EEEEF1] bg-[#F7F7F9]">
            <PostImage src={image} className="w-full max-h-[280px]" />
          </div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-1 gap-3">
          <InfoTile Icon={CalendarClock} label="Date & time" value={when.isValid() ? when.format("ddd, DD MMM YYYY") : "—"} sub={when.isValid() ? `${when.format("hh:mm A")} · ${when.fromNow()}` : ""} />
          <InfoTile
            Icon={MapPin}
            label="Location"
            value={e.location?.text || "No location"}
            sub={mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="no-underline inline-flex items-center gap-1 text-[#EA352B] font-semibold">
                Open in Maps <ExternalLink size={11} />
              </a>
            )}
          />
          <InfoTile Icon={Users} label="Going" value={`${going.length} ${going.length === 1 ? "person" : "people"}`} sub={`Created ${fromNow(e.createdAt)}`} />
        </div>

        <DialogSection title="About this event">
          <p className="m-0 text-sm text-[#374151] leading-relaxed whitespace-pre-line">
            {e.description || <span className="text-[#9CA3AF] italic">No description</span>}
          </p>
        </DialogSection>

        {goingUsers.length > 0 && (
          <DialogSection title={`Attendees · ${going.length}`}>
            <div className="flex flex-wrap gap-2">
              {goingUsers.slice(0, 12).map((u, i) => (
                <span key={u._id || i} className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F9] pl-1 pr-3 py-1 text-xs text-[#374151]">
                  <Avatar src={getAvatarUrl(u)} size={22} /> {getAuthorName(u)}
                </span>
              ))}
              {going.length > 12 && <span className="self-center text-xs text-[#9CA3AF]">+{going.length - 12} more</span>}
            </div>
          </DialogSection>
        )}

        {e.tags?.length > 0 && (
          <DialogSection title="Tags">
            <div className="flex gap-2 flex-wrap">
              {e.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#F7F7F9] px-3 py-1 text-xs text-[#374151]"><TagIcon size={11} className="text-[#EA352B]" /> {t}</span>
              ))}
            </div>
          </DialogSection>
        )}
      </div>

      <DialogFooter>
        {key === "pending" && (
          <>
            <ActionBtn Icon={Ban} label="Reject" onClick={onReject} variant="outline" loading={busy === `reject:${e._id}`} disabled={!!busy} />
            <ActionBtn Icon={CheckCircle2} label="Approve & publish" onClick={onApprove} variant="success" loading={busy === `approve:${e._id}`} disabled={!!busy} />
          </>
        )}
        {key === "live" && (
          <>
            <ActionBtn Icon={RotateCcw} label="Revoke approval" onClick={onReject} variant="outline" loading={busy === `reject:${e._id}`} disabled={!!busy} />
            <ActionBtn Icon={Trash2} label="Delete event" onClick={onDelete} variant="danger" disabled={!!busy} />
          </>
        )}
        {key === "rejected" && (
          <>
            <ActionBtn Icon={Trash2} label="Delete" onClick={onDelete} variant="outline" disabled={!!busy} />
            <ActionBtn Icon={CheckCircle2} label="Approve" onClick={onApprove} variant="success" loading={busy === `approve:${e._id}`} disabled={!!busy} />
          </>
        )}
      </DialogFooter>
    </DialogFrame>
  );
}

function InfoTile({ Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-[#fff] border border-[#EEEEF1] p-4 min-w-0">
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#9CA3AF]">
        <Icon size={12} className="text-[#EA352B]" /> {label}
      </span>
      <p className="m-0 mt-1.5 text-sm font-semibold text-[#111]" title={typeof value === "string" ? value : undefined}>{value}</p>
      {sub && <p className="m-0 mt-0.5 text-[11px] text-[#9CA3AF]">{sub}</p>}
    </div>
  );
}

function ConfirmDelete({ target, busy, onCancel, onConfirm }) {
  useDialog(!!target && !busy, onCancel);
  if (!target) return null;
  return (
    <DialogFrame labelledBy="confirm-title" onClose={busy ? () => {} : onCancel} maxWidth={420}>
      <div className="p-6 bg-[#fff]">
        <span className="w-14 h-14 rounded-2xl bg-[#FDECEA] flex items-center justify-center">
          <Trash2 size={24} className="text-[#EA352B]" />
        </span>
        <h3 id="confirm-title" className="m-0 mt-4 text-lg font-medium text-[#111]" style={FONT_HEADING}>Delete this {target.type}?</h3>
        <p className="m-0 mt-2 text-sm text-[#6B7280] leading-relaxed">
          {target.title && <><b className="text-[#111]">{target.title}</b> — </>}
          this will be removed from the app permanently. This can’t be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onCancel} disabled={busy} className="flex-1 rounded-xl border border-[#EEEEF1] py-2.5 text-sm font-semibold text-[#374151] hover:bg-[#F7F7F9] transition disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#EA352B] py-2.5 text-sm font-semibold text-[#fff] hover:bg-[#D42F26] transition disabled:opacity-70">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete
          </button>
        </div>
      </div>
    </DialogFrame>
  );
}
